import React, { useState } from 'react';
import { Button, Modal, Select, Slider } from 'antd';
import FileUploadButton, { FileUploadButtonPros } from './FileUploadButton';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { getCroppedImageBlob } from '@utils/image';
import { extractFileName } from '@utils/string';
import 'react-easy-crop/react-easy-crop.css';

interface CropFileUploadButtonProps extends Omit<FileUploadButtonPros, 'onChange'> {
  /** 최종 선택(크롭/그대로)된 파일 리스트를 전달합니다. 보통 첫 번째 파일만 사용합니다. */
  onChangeFiles: (files: FileList) => void;
  /** 모달 제목 (선택) */
  modalTitleChoice?: string;
  modalTitleCrop?: string;
}

export const CropFileUploadButton: React.FC<CropFileUploadButtonProps> = (props) => {
  const { onChangeFiles, modalTitleChoice, modalTitleCrop, ...buttonProps } = props;

  const [uploadState, setUploadState] = useState<{
    file: File;
    previewUrl: string;
  } | null>(null);
  const [step, setStep] = useState<'choice' | 'crop'>('choice');
  const [cropAreaPixels, setCropAreaPixels] = useState<Area | null>(null);
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [aspect, setAspect] = useState<number>(1);

  const resetState = () => {
    if (uploadState) {
      URL.revokeObjectURL(uploadState.previewUrl);
    }
    setUploadState(null);
    setStep('choice');
    setCropAreaPixels(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setAspect(1);
  };

  const handleInternalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    const file = event.target.files[0];
    const previewUrl = URL.createObjectURL(file);
    setUploadState({ file, previewUrl });
    setStep('choice');
    setCropAreaPixels(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setAspect(1);
  };

  const handleUploadOriginal = () => {
    if (!uploadState) return;
    const dt = new DataTransfer();
    dt.items.add(uploadState.file);
    onChangeFiles(dt.files);
    resetState();
  };

  const handleApplyCrop = async () => {
    if (!uploadState || !cropAreaPixels) return;
    try {
      const blob = await getCroppedImageBlob(
        uploadState.previewUrl,
        cropAreaPixels,
        uploadState.file.type.startsWith('image/') ? uploadState.file.type : 'image/jpeg',
      );
      const { fileName } = extractFileName(uploadState.file.name);
      const ext = blob.type.split('/')[1] || 'jpg';
      const file = new File([blob], `${fileName}.${ext}`);
      const dt = new DataTransfer();
      dt.items.add(file);
      onChangeFiles(dt.files);
    } finally {
      resetState();
    }
  };

  return (
    <>
      <FileUploadButton {...buttonProps} onChange={handleInternalChange} />
      <Modal
        title={step === 'choice' ? modalTitleChoice || '이미지 업로드' : modalTitleCrop || '이미지 크롭'}
        open={!!uploadState}
        onCancel={resetState}
        footer={null}
        width={step === 'crop' ? 520 : 360}
        destroyOnClose>
        {uploadState && (
          <>
            {step === 'choice' ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                <img
                  src={uploadState.previewUrl}
                  alt="미리보기"
                  style={{
                    maxWidth: '320px',
                    maxHeight: '240px',
                    objectFit: 'contain',
                    border: '1px solid #eaeaea',
                    borderRadius: '8px',
                  }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button type="primary" onClick={handleUploadOriginal}>
                    그대로 업로드
                  </Button>
                  <Button
                    onClick={() => {
                      setStep('crop');
                    }}>
                    크롭 후 업로드
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ position: 'relative', height: '460px', background: '#000', paddingTop: '8px' }}>
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    left: 16,
                    right: 16,
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 16,
                    color: '#fff',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12 }}>줌</span>
                    <Slider
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(value) => {
                        if (typeof value === 'number') {
                          setZoom(value);
                        }
                      }}
                      style={{ width: 160 }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12 }}>비율</span>
                    <Select
                      size="small"
                      value={aspect}
                      style={{ width: 120 }}
                      onChange={(value) => {
                        setAspect(value);
                      }}
                      options={[
                        { label: '1:1', value: 1 },
                        { label: '4:3', value: 4 / 3 },
                        { label: '16:9', value: 16 / 9 },
                      ]}
                    />
                  </div>
                </div>
                <Cropper
                  image={uploadState.previewUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_croppedArea, croppedAreaPixelsValue) => {
                    setCropAreaPixels(croppedAreaPixelsValue);
                  }}
                  onCropAreaChange={(_croppedArea, croppedAreaPixelsValue) => {
                    setCropAreaPixels(croppedAreaPixelsValue);
                  }}
                  style={{ containerStyle: { background: '#000' } }}
                  classes={{}}
                  cropShape="rect"
                  showGrid={false}
                  zoomSpeed={1.2}
                  restrictPosition
                  mediaProps={{}}
                  cropperProps={{}}
                  minZoom={1}
                  maxZoom={3}
                  rotation={0}
                  onRotationChange={() => {}}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: 16,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 8,
                  }}>
                  <Button
                    onClick={() => {
                      setStep('choice');
                    }}>
                    취소
                  </Button>
                  <Button type="primary" disabled={!cropAreaPixels} onClick={handleApplyCrop}>
                    적용
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default CropFileUploadButton;
